import { authApi } from "@api";
import { toast } from "@components/utils/Toaster";
import { RegistrationMethod, useStatsigTracking } from "@configs/statsig";
import useContactLinks from "@hooks/useContactLinks";
import { GoogleAuthMethod, useGoogleAuthMethod } from "@hooks/useGoogleAuthMethod";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { AuthToken, LoginGrantType, PropsWithClassName, cn } from "kz-ui-sdk";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FirebaseGoogleAuthButton, GoogleAuthButton } from "./GoogleAuthButton";

interface AuthSocialProps extends PropsWithClassName {
  mode: "login" | "register";
  title?: string;
  onLoginSuccess?: (token?: AuthToken) => void;
}

const AuthSocial = ({ mode, title, className, onLoginSuccess }: AuthSocialProps) => {
  const { isSupportAuthSocial } = useMemberSettings();
  const googleAuthMethod = useGoogleAuthMethod();
  const { t } = useTranslation();
  const { openContact } = useContactLinks();
  const { trackRegistrationComplete, trackRegistrationFailed } = useStatsigTracking();
  const [fetchProfile] = authApi.useLazyGetProfileWithTokenQuery();

  const wrappedOnLoginSuccess = useCallback(
    async (token?: AuthToken) => {
      if (mode === "register" && token?.access_token) {
        try {
          const profile = await fetchProfile(token.access_token).unwrap();
          trackRegistrationComplete(RegistrationMethod.GOOGLE_LOGIN, {
            member: profile?.self?.member,
          });
        } catch (error) {
          // Profile fetch failed, but still call original callback
          // Registration tracking will be incomplete, but login should proceed
        }
      }
      onLoginSuccess?.(token);
    },
    [mode, fetchProfile, trackRegistrationComplete, onLoginSuccess],
  );

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: wrappedOnLoginSuccess,
    suppressLoginTrack: mode === "register",
  });

  const handleOpenContactCsDialog = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const handleGoogleLoginSuccess = useCallback(
    async (idToken: string) => {
      const response = await handleLogin(idToken, idToken, LoginGrantType.Google);
      if (mode === "register" && response?.error) {
        trackRegistrationFailed(RegistrationMethod.GOOGLE_LOGIN, response.error);
      }
    },
    [handleLogin, mode, trackRegistrationFailed],
  );

  const handleFirebaseLoginSuccess = useCallback(
    async (idToken: string) => {
      // Firebase uses LoginGrantType.GoogleFirebase grant type
      const response = await handleLogin(idToken, idToken, LoginGrantType.GoogleFirebase);
      if (mode === "register" && response?.error) {
        trackRegistrationFailed(RegistrationMethod.GOOGLE_LOGIN, response.error);
      }
    },
    [handleLogin, mode, trackRegistrationFailed],
  );

  if (!isSupportAuthSocial) {
    return null;
  }

  const handleCommonError = () => {
    toast.warningWithClose(
      <span className="">
        <span>{t("something went wrong. please retry or")}</span>{" "}
        <span
          onClick={handleOpenContactCsDialog}
          className="cursor-pointer text-content-soft underline"
        >
          {t("contact cs")}
        </span>
      </span>,
      {
        duration: 8000,
      },
    );
  };

  const handleGoogleLoginError = () => {
    handleCommonError();
  };

  return (
    <div className={cn("flex w-full flex-col items-center", className)}>
      {!!title && <p className="mb-2 text-base leading-6 text-content-soft">{title}</p>}
      {googleAuthMethod === GoogleAuthMethod.Firebase && (
        <FirebaseGoogleAuthButton
          mode={mode}
          loading={isLoginLoading}
          onOAuthLoginSuccess={handleFirebaseLoginSuccess}
          onOAuthLoginError={handleGoogleLoginError}
        />
      )}
      {googleAuthMethod === GoogleAuthMethod.GoogleOAuth && (
        <GoogleAuthButton
          mode={mode}
          loading={isLoginLoading}
          onOAuthLoginSuccess={handleGoogleLoginSuccess}
          onOAuthLoginError={handleGoogleLoginError}
        />
      )}
    </div>
  );
};

export default AuthSocial;
